import { useRouter } from 'next/router';
import * as React from 'react';

import type { AskMessage } from '../types';

export function useOpenResultsPage({
  askMessages,
  setAskMessages,
  setAskError,
}: {
  askMessages: AskMessage[];
  setAskMessages: React.Dispatch<React.SetStateAction<AskMessage[]>>;
  setAskError: React.Dispatch<React.SetStateAction<string>>;
}) {
  const router = useRouter();
  const [viewResultsLoadingIndex, setViewResultsLoadingIndex] = React.useState<
    number | null
  >(null);

  const handleViewResults = React.useCallback(
    async (messageIndex: number) => {
      const msg = askMessages[messageIndex];
      if (!msg || msg.role !== 'assistant') return;

      const clearInlineError = () => {
        setAskError('');
        setAskMessages((prev) => {
          const updated = [...prev];
          const current = updated[messageIndex];
          if (!current || current.role !== 'assistant') return prev;
          updated[messageIndex] = {
            ...current,
            viewResultsError: null,
          };
          return updated;
        });
      };

      const setInlineError = (errMsg: string) => {
        setAskMessages((prev) => {
          const updated = [...prev];
          const current = updated[messageIndex];
          if (!current || current.role !== 'assistant') return prev;
          updated[messageIndex] = {
            ...current,
            viewResultsError: errMsg,
          };
          return updated;
        });
      };

      // Keyword Ask → InstantSearch page (reuse same button entrypoint).
      if (msg.responseType === 'search_redirect') {
        const priorUser =
          messageIndex > 0 ? askMessages[messageIndex - 1] : null;
        const query =
          msg.originalQuery.trim() ||
          (priorUser?.role === 'user' ? priorUser.text.trim() : '');
        if (!query) {
          setAskError('No search query available for this message.');
          return;
        }

        clearInlineError();
        setViewResultsLoadingIndex(messageIndex);
        try {
          await router.push({
            pathname: '/search',
            query: { query },
          });
        } catch (e) {
          const errMsg = e instanceof Error ? e.message : String(e);
          setInlineError(`View Search Results failed: ${errMsg}`);
        } finally {
          setViewResultsLoadingIndex(null);
        }
        return;
      }

      if (!msg.queryToken) {
        setAskError(
          'No query token — enable AI_ASK_QUERY_TOKEN_ENABLED on the server.'
        );
        return;
      }

      clearInlineError();
      setViewResultsLoadingIndex(messageIndex);

      try {
        await router.push({
          pathname: '/aiAskResults',
          query: { queryToken: msg.queryToken },
        });
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e);
        setInlineError(`View Results failed: ${errMsg}`);
      } finally {
        setViewResultsLoadingIndex(null);
      }
    },
    [askMessages, router, setAskError, setAskMessages]
  );

  return {
    viewResultsLoadingIndex,
    handleViewResults,
  };
}
